import { Box, IconButton } from '@chakra-ui/react';
import { MdMoreVert } from 'react-icons/md';

import { ScanningTotal, StandsTotal } from '@app/api/models';
import { TableColumn } from '@app/components/common/table';
import { Txt } from '@app/components/common';
import { Level } from '@app/api/models/structure';

import { StandsNodeItem } from './types';
import { StandsReportViewNode } from './components/node';

export const useColumns = ({ chain, levels, onItemClick, onMoveUpClick, onShowDetail }: { 
  chain: StandsNodeItem[],
  levels: Level[],
  onItemClick: (item: StandsNodeItem) => void
  onMoveUpClick: (item: StandsNodeItem) => void
  onShowDetail: (item: StandsNodeItem) => void
}): TableColumn<StandsNodeItem>[] => {
  return [
    {
      name: 'id',
      label: '#',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (name, { rowData, totalRows, rowIndex }) => {
          if (rowData.id === '-1') {
            return <Txt color='gray.500'>Итого</Txt>;
          }
          if (!chain.length) {
            return <StandsReportViewNode
              data={rowData}
              isLastLevel={false}
              index={0}
              parentIndex={0}
              onItemClick={onItemClick}
            />;
          }
          const index = chain.findIndex(({ id, level }) => id === rowData.id && level.id === rowData.level.id);
          const isLastLevel = levels[levels.length - 1].value === rowData.level.id;
          const levelIndex = levels.findIndex(({ value }) => value === rowData.level.id);

          if (index !== -1) {
            const parentLevelIndex = index > 0 ? levels.findIndex(({ value }) => value === chain[index - 1].level.id) : -1; 

            return <StandsReportViewNode               
              data={rowData}
              isLastLevel={false}
              index={levelIndex}
              parentIndex={parentLevelIndex}
              onItemClick={onMoveUpClick}
              linkUp={index > 0 ? 1 : undefined}
              linkDown
            />;
          }
          const parentLevelIndex = levels.findIndex(({ value }) => value === chain[chain.length - 1].level.id);  

          return <StandsReportViewNode
            data={rowData}
            index={levelIndex}
            parentIndex={parentLevelIndex}
            isLastLevel={isLastLevel}
            onItemClick={onItemClick}
            linkUp={1}
            linkMore={totalRows > rowIndex + 2} 
          />;
        },
      },
    },
    {
      name: 'total',
      label: 'Сканирование',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: StandsTotal) =>  {
          return <Box display='flex' alignItems='center' justifyContent='flex-start'>
            <Txt fontWeight='bold'>{value.check}</Txt>
          </Box>;
        },
      },
    },
    {
      name: 'total',
      label: 'Установки',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: StandsTotal) =>  {
          return <Box display='flex' alignItems='center' justifyContent='flex-start'>
            <Txt fontWeight='bold'>{value.mount}</Txt>
          </Box>;
        },
      },
    },
    {
      name: 'total',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: StandsTotal, { rowData }) =>  {
          if (rowData.id === '-1') {return null;}
          return <Box display='flex' alignItems='center'>
            <Box ml={1}>
              <IconButton aria-label='' onClick={() => onShowDetail(rowData)}>
                <MdMoreVert />
              </IconButton>
            </Box>
          </Box>;
        },
      },
    },
    // filters
    {
      name: 'stand_id',
      label: 'ID стойки',
      options: {
        hidden: true,
        filter: {
          type: 'text',
        },
        sort: false,
      }
    }, 
    {
      name: 'model_id',
      label: 'Модель',
      options: {
        hidden: true,
        filter: {
          type: 'select',
          name: 'model_id',
          sourceName: 'stand_model',
        },
        sort: false,
      }
    }, 
    {
      name: 'brand_id',
      label: 'Бренд',
      options: {
        hidden: true,
        filter: {
          type: 'select',
          name: 'brand_id',
          sourceName: 'stand_brand',
        },
        sort: false,
      }
    },
    {
      name: 'manufacturer_id',
      label: 'Производитель',
      options: {
        hidden: true,
        filter: {
          type: 'select',
          name: 'manufacturer',
          sourceName: 'equipment_manufacturer',
        },
        sort: false,
      }
    },
    {
      name: 'equip_model_id',
      label: 'Модель стойки',
      options: {
        hidden: true,
        filter: {
          type: 'select',
          name: 'equip_model_id',
          sourceName: 'stand_equip_model',
        },
        sort: false,
      }
    },
    {
      name: 'director',
      label: 'Директор',
      options: {
        hidden: true,
        filter: {
          type: 'text',
          name: 'director',
        },
        sort: false,
      }
    },
    {
      name: 'regional_manager',
      label: 'Региональный менеджер',
      options: {
        hidden: true,
        filter: {
          type: 'text',
          name: 'regional_manager',
        },
        sort: false,
      }
    }, 
    {
      name: 'territorial_manager',
      label: 'Территориальный менеджер',
      options: {
        hidden: true,
        filter: {
          type: 'text',
          name: 'territorial_manager',
        },
        sort: false,
      }
    }, 
    {
      name: 'distributor',
      label: 'Дистрибьютор',
      options: {
        hidden: true,
        filter: {
          type: 'autocomplete',          
          sourceName: 'distributors',
        },
        sort: false,
      }
    }, 
    {
      name: 'route_number',
      label: 'Номер маршрута',
      options: {
        hidden: true,
        filter: {
          type: 'autocomplete-paginated',
          sourceName: 'route_number',
        },
        sort: false,
      }
    }, 
    {
      name: 'outlet',
      label: 'Торговая точка',
      options: {
        hidden: true,
        filter: {
          type: 'outlet',
        },
        sort: false,
      }
    }, 
    {
      name: 'mount_dt',
      label: 'Дата установки',
      options: {
        hidden: true,
        filter: {
          type: 'date',
          name: 'mount_dt',
        },
        sort: false,
      }
    }, 
    {
      name: 'update_dt',
      label: 'Дата обновления',
      options: {
        hidden: true,
        filter: {
          type: 'date',
          name: 'update_dt',
        },
        sort: false,
      }
    },
    {
      name: 'full_name',
      label: 'Пользователь (ФИО)',
      options: {
        hidden: true,
        filter: {
          type: 'text',
          name: 'full_name',
        },
        sort: false,
      },
    }, 
  ];
};


// full_name: getFilterValue(filter, 'full_name'),
// manufacturer_id: getArrayFilterValue<StandsNodeItem>(filter, 'manufacturer'),
// model_id: getArrayFilterValue<StandsNodeItem>(filter, 'model'),
// brand_id: getArrayFilterValue<StandsNodeItem>(filter, 'brand'),
// equip_model_id: getArrayFilterValue<StandsNodeItem>(filter, 'equip_model'),
// director: getFilterValue<StandsNodeItem>(filter, 'director'),
// regional_manager: getFilterValue<StandsNodeItem>(filter, 'regional_manager'),
// territorial_manager: getFilterValue<StandsNodeItem>(filter, 'territorial_manager'),
// distributor_id: Number(getFilterValue<StandsNodeItem>(filter, 'distributor')),
// route_number_id: Number(getFilterValue<StandsNodeItem>(filter, 'router_number')),
// outlet_id: Number(getFilterValue<StandsNodeItem>(filter, 'outlet')),
// stand_id: Number(getFilterValue<StandsNodeItem>(filter, 'stand_id')),
// is_active: !!getFilterValue<StandsNodeItem>(filter, 'is_active'),
// mount_dt: getFilterValue<StandsNodeItem>(filter, 'mount_dt'),
// update_dt: getFilterValue<StandsNodeItem>(filter, 'update_dt'),